import { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

export interface Branding {
  id: string;
  name: string;
  slug?: string;
  logoUrl?: string | null;
}

const STORAGE_KEY = 'activeBranding';

function loadCached(): Branding | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Branding) : null;
  } catch {
    return null;
  }
}

function saveCached(b: Branding | null) {
  if (!b) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, JSON.stringify(b));
}

/**
 * Returns the active property group's branding (name + logo) for the current
 * user. Resolution order:
 *   - ADMIN: selectedPropertyGroup from localStorage (set by GroupSlugWrapper)
 *   - RESIDENT / VENDOR: user.propertyGroupId
 *   - PLATFORM_ADMIN / no group: returns null branding
 *
 * Cached in localStorage so the logo paints instantly on next page load
 * before the network call completes.
 */
export function useBranding(): { branding: Branding | null; refresh: () => void } {
  const { user } = useAuth();
  const [branding, setBranding] = useState<Branding | null>(() => loadCached());

  const resolveGroupId = useCallback((): string | null => {
    if (!user) return null;
    if (user.role === 'ADMIN') {
      return localStorage.getItem('selectedPropertyGroup');
    }
    if (user.role === 'RESIDENT' || user.role === 'VENDOR') {
      return user.propertyGroupId || user.hoaId || null;
    }
    return null;
  }, [user]);

  const refresh = useCallback(async () => {
    const groupId = resolveGroupId();
    if (!groupId) {
      setBranding(null);
      saveCached(null);
      return;
    }
    try {
      const res = await api.get<Branding>(`/api/property-groups/${groupId}/branding`);
      setBranding(res.data);
      saveCached(res.data);
    } catch {
      // leave cached value in place on transient errors
    }
  }, [resolveGroupId]);

  useEffect(() => {
    const groupId = resolveGroupId();
    // If cached branding belongs to a different group, drop it first so we
    // don't briefly show the wrong logo on an admin who switched groups.
    if (branding && groupId && branding.id !== groupId) {
      setBranding(null);
      saveCached(null);
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.propertyGroupId, refresh]);

  return { branding, refresh };
}
